import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, BookOpen, Check, Lock } from 'lucide-react';
import { MaschaSkinsData } from './MaschaSkinsData';
interface MaschaStorylineModalProps {
  isOpen: boolean;
  onClose: () => void;
  unlockedSkins: string[];
}
export const MaschaStorylineModal: React.FC<MaschaStorylineModalProps> = ({
  isOpen,
  onClose,
  unlockedSkins
}) => {
  const [activeChapter, setActiveChapter] = useState(0);
  if (!isOpen) return null;
  // Find the maximum chapter that can be viewed based on unlocked skins
  const maxChapter = MaschaSkinsData.reduce((max, skin, index) => {
    return unlockedSkins.includes(skin.id) ? index : max;
  }, -1);
  const handleNextChapter = () => {
    if (activeChapter < maxChapter) {
      setActiveChapter(activeChapter + 1);
    }
  };
  const handlePrevChapter = () => {
    if (activeChapter > 0) {
      setActiveChapter(activeChapter - 1);
    }
  };
  const currentSkin = MaschaSkinsData[activeChapter];
  const isCurrentChapterUnlocked = unlockedSkins.includes(currentSkin?.id);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-9491">
      <div className="relative w-full max-w-2xl bg-gradient-to-b from-gray-900 to-black rounded-xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-amber-800/30" data-id="element-9492">
        <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" onClick={onClose} data-id="element-9493">
          <X size={18} data-id="element-9494" />
        </button>
        <div className="p-6" data-id="element-9495">
          {/* Header */}
          <div className="flex items-center justify-between mb-6" data-id="element-9496">
            <div className="flex items-center gap-3" data-id="element-9497">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-600 to-red-600 flex items-center justify-center" data-id="element-9498">
                <BookOpen size={18} className="text-white" data-id="element-9499" />
              </div>
              <div data-id="element-9500">
                <h2 className="text-xl font-bold text-amber-400" data-id="element-9501">
                  Mascha's Storyline
                </h2>
                <p className="text-gray-400 text-sm" data-id="element-9502">
                  Chapter {activeChapter + 1} of {MaschaSkinsData.length}
                </p>
              </div>
            </div>
          </div>

          {/* Chapter navigation */}
          <div className="flex items-center justify-between mb-4" data-id="element-9503">
            <button onClick={handlePrevChapter} disabled={activeChapter === 0} className={`w-8 h-8 flex items-center justify-center rounded-full ${activeChapter === 0 ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-amber-900/50 text-amber-500 hover:bg-amber-900/70'}`} data-id="element-9504">
              <ChevronLeft size={18} data-id="element-9505" />
            </button>
            <div className="flex-1 mx-4" data-id="element-9506">
              <div className="h-1 bg-gray-800 rounded-full overflow-hidden" data-id="element-9507">
                <div className="h-full bg-gradient-to-r from-amber-600 to-red-600 transition-all duration-300" style={{
                width: `${(activeChapter + 1) / MaschaSkinsData.length * 100}%`
              }} data-id="element-9508"></div>
              </div>
            </div>
            <button onClick={handleNextChapter} disabled={activeChapter >= maxChapter} className={`w-8 h-8 flex items-center justify-center rounded-full ${activeChapter >= maxChapter ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-amber-900/50 text-amber-500 hover:bg-amber-900/70'}`} data-id="element-9509">
              <ChevronRight size={18} data-id="element-9510" />
            </button>
          </div>

          {/* Chapter title */}
          <div className="flex items-center justify-between mb-4" data-id="element-9511">
            <h3 className="text-lg font-bold text-white" data-id="element-9512">
              {currentSkin?.skin_name}
            </h3>
            {isCurrentChapterUnlocked ? <div className="px-2 py-1 bg-green-900/30 text-green-500 rounded-full text-xs flex items-center" data-id="element-9513">
                <Check size={12} className="mr-1" data-id="element-9514" />
                Unlocked
              </div> : <div className="px-2 py-1 bg-gray-800 text-gray-400 rounded-full text-xs flex items-center" data-id="element-9515">
                <Lock size={12} className="mr-1" data-id="element-9516" />
                Locked
              </div>}
          </div>

          {/* Chapter content */}
          <div className="relative" data-id="element-9517">
            <div className="flex mb-4" data-id="element-9518">
              <div className="w-1/3 pr-4" data-id="element-9519">
                <div className="aspect-[3/4] rounded-lg overflow-hidden" data-id="element-9520">
                  <img src={currentSkin?.preview_image_url} alt={currentSkin?.skin_name} className={`w-full h-full object-cover object-top ${!isCurrentChapterUnlocked ? 'grayscale opacity-70' : ''}`} data-id="element-9521" />
                </div>
              </div>
              <div className="w-2/3" data-id="element-9522">
                {isCurrentChapterUnlocked ? <div className="bg-black/40 rounded-lg p-4 border border-amber-800/30 h-full" data-id="element-9523">
                    <p className="text-gray-300 leading-relaxed italic" data-id="element-9524">
                      {currentSkin?.storyline_piece}
                    </p>
                  </div> : <div className="bg-black/40 rounded-lg p-4 border border-gray-800/30 h-full flex flex-col items-center justify-center text-center" data-id="element-9525">
                    <Lock size={24} className="text-gray-500 mb-2" data-id="element-9526" />
                    <h4 className="text-gray-400 font-medium mb-1" data-id="element-9527">
                      Chapter Locked
                    </h4>
                    <p className="text-gray-500 text-sm" data-id="element-9528">
                      Unlock the {currentSkin?.skin_name} skin to reveal this
                      part of Mascha's story.
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Chapter selection */}
          <div className="mt-6" data-id="element-9529">
            <h4 className="text-sm font-medium text-gray-400 mb-3" data-id="element-9530">
              All Chapters
            </h4>
            <div className="flex flex-wrap gap-2" data-id="element-9531">
              {MaschaSkinsData.map((skin, index) => {
              const isSkinUnlocked = unlockedSkins.includes(skin.id);
              return <button key={skin.id} onClick={() => {
                if (isSkinUnlocked || index <= maxChapter) {
                  setActiveChapter(index);
                }
              }} disabled={!isSkinUnlocked && index > maxChapter} className={`w-10 h-10 rounded-full flex items-center justify-center ${activeChapter === index ? 'bg-gradient-to-r from-amber-600 to-red-600 text-white' : isSkinUnlocked || index <= maxChapter ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-900 text-gray-600 cursor-not-allowed'}`} data-id="element-9532">
                    {index + 1}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>
    </div>;
};